import { z } from 'zod'
import {
  UserSchema,
  CurrentUserSchema,
  DealroomUserSchema,
  RegistrationSchema,
  OrganizationRegistrationSchema,
  CandidateResponseSchema,
  DealUsershipSchema,
} from '../schemas'
import { DealroomUserData, UserState } from '../types'
import { http } from './http'
import { auth } from './auth'
import { parseWithZod } from '../utils/parse-with-zod'

const UsersResponseSchema = z.object({
  users: z.array(
    UserSchema.pick({
      email: true,
      first_name: true,
      id: true,
      state: true,
      last_name: true,
    }),
  ),
})

const userDeletionSchema = z.object({
  deletion_date: z.string().nullable(),
})

export const DealroomUserResponseSchema = z.object({
  user: DealroomUserSchema,
})

export const CurrentUserResponseSchema = z.object({
  user: CurrentUserSchema,
})

export const CurrentUsershipsResponseSchema = z.object({
  deal_userships: DealUsershipSchema.array(),
})

const BulkInviteResponseSchema = z.object({
  failed: z.array(
    z.object({
      email: z.string(),
      reason: z.string(),
    }),
  ),
  succeeded: z.array(z.string()),
})

const RegistrationsResponseSchema = z.array(RegistrationSchema)
const OrganizationRegistrationsResponseSchema = z.array(OrganizationRegistrationSchema)

export const DealUsershipsResponseSchema = z.object({
  deal_userships: z.array(DealUsershipSchema),
})

type BulkInviteResponse = z.infer<typeof BulkInviteResponseSchema>
type RegistrationResponse = z.infer<typeof RegistrationSchema>

export const users = {
  async getAllByOrgId(orgId: number) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/users`, {
      organization__auth_organization_id: orgId,
      state: UserState.ACTIVE,
    })

    const parsedResult = parseWithZod(UsersResponseSchema, result)
    return parsedResult.users
  },

  async getById(userId: number) {
    const result = await http.get(`/api/v1/users/${userId}`)
    return parseWithZod(UserSchema, result)
  },

  async getByDealroomUserId(dealroomUserId: number) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/users/${dealroomUserId}`)
    return parseWithZod(DealroomUserResponseSchema, result)
  },

  async getDealroomUserByAuthUserId(authUserId: number) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/users/by_auth_id/${authUserId}`)
    return parseWithZod(DealroomUserResponseSchema, result)
  },

  async getByDealId(dealId: number, host?: string) {
    const regionHost = host ? `${location.protocol}//${host}` : auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/users?deal=${dealId}`)
    const parsedResult = parseWithZod(UsersResponseSchema, result)
    return parsedResult.users
  },

  async getCurrent(regionHost?: string) {
    const host = regionHost ? `${location.protocol}//${regionHost}` : auth.getUserRegionHost()
    const result = await http.get(`${host}/api/users/current`)
    const parsedResult = parseWithZod(CurrentUserResponseSchema, result)
    return parsedResult.user
  },

  async getCurrentUserships(regionHost?: string) {
    const host = regionHost ? `${location.protocol}//${regionHost}` : auth.getUserRegionHost()
    const result = await http.get(`${host}/api/users/current_userships`)
    const parsedResult = parseWithZod(CurrentUsershipsResponseSchema, result)
    return parsedResult
  },

  async getActiveUsers() {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/users`, {
      state: UserState.ACTIVE,
    })
    const parsedResult = parseWithZod(UsersResponseSchema, result)
    return parsedResult.users
  },

  async getDealResponsibleLawyerCandidates(dealId: number, regionHost?: string) {
    const host = regionHost ? `${location.protocol}//${regionHost}` : auth.getUserRegionHost()
    const result = await http.get(
      `${host}/api/deals/${dealId}/responsible_lawyer_candidates`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(CandidateResponseSchema, result)
    return parsedResult.users
  },

  async getOrgResponsibleLawyerCandidates() {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/users/responsible_lawyer_candidates`)
    const parsedResult = parseWithZod(CandidateResponseSchema, result)
    return parsedResult.users
  },

  async getFileRecipientCandidates(dealId: number) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/file_recipient_candidates`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(CandidateResponseSchema, result)
    return parsedResult.users
  },

  async updatePreferences(
    userId: number,
    preferences: {
      default_email_frequency?: string
      locale?: string
      notify_about_email_uploads?: boolean
    },
  ) {
    const result = await http.put(`/api/v1/users/${userId}`, preferences)
    return parseWithZod(UserSchema, result)
  },

  async enableOrgAdminAccess(user: DealroomUserData) {
    return await http.post(`/api/v1/users/${user.auth_user_id}/enable_org_admin_access`)
  },

  async disableOrgAdminAccess(user: DealroomUserData) {
    return await http.post(`/api/v1/users/${user.auth_user_id}/disable_org_admin_access`)
  },

  async deactivateUser(user: DealroomUserData) {
    const result = await http.post(`/api/v1/users/${user.auth_user_id}/deactivate_user`)
    const parsedResult = parseWithZod(UserSchema, result)
    return parsedResult
  },

  async reactivateUser(user: DealroomUserData) {
    const result = await http.post(`/api/v1/users/${user.auth_user_id}/reactivate_user`)
    const parsedResult = parseWithZod(UserSchema, result)
    return parsedResult
  },

  async scheduleDeletion(user: DealroomUserData) {
    const result = await http.post(`/api/v1/users/${user.auth_user_id}/schedule_user_delete`)
    const parsedResult = parseWithZod(userDeletionSchema, result)
    return parsedResult
  },

  async unscheduleDeletion(user: DealroomUserData) {
    const result = await http.post(`/api/v1/users/${user.auth_user_id}/unschedule_user_delete`)
    const parsedResult = parseWithZod(userDeletionSchema, result)
    return parsedResult
  },

  async bulkInvite(emails: string[], invitedById: number): Promise<BulkInviteResponse> {
    const result = await http.post(`/api/v1/registrations/bulk_invite`, {
      emails,
      invited_by_id: invitedById,
    })
    return parseWithZod(BulkInviteResponseSchema, result)
  },

  async getInternalRegistrations() {
    const result = await http.get(`/api/v1/registrations/internal_registrations`) // TODO: the name of this endpoint may change?
    return parseWithZod(RegistrationsResponseSchema, result)
  },

  async getOrganizationRegistrations() {
    const result = await http.get(`/api/v1/registrations/organization_registrations`)
    return parseWithZod(OrganizationRegistrationsResponseSchema, result)
  },

  async resendActivation(email: string, invitedById: number): Promise<RegistrationResponse> {
    const result = await http.post('/api/v1/registrations/resend_activation', {
      email,
      invited_by_id: invitedById,
    })
    return parseWithZod(RegistrationSchema, result)
  },

  async revokeActivation(email: string): Promise<RegistrationResponse> {
    const result = await http.post('/api/v1/registrations/revoke_activation', {
      email,
    })
    return parseWithZod(RegistrationSchema, result)
  },

  // Invitee-facing (unauthenticated): an invited user redeeming their own activation key. The
  // admin-facing registration endpoints above require an org-admin session; these two do not.
  async getRegistration(activationKey: string): Promise<RegistrationResponse> {
    const result = await http.get(`/api/v1/registrations/${activationKey}`)
    return parseWithZod(RegistrationSchema, result)
  },

  async completeRegistration(
    activationKey: string,
    payload: {
      first_name: string
      last_name: string
      new_password1: string
      new_password2: string
      subscribe_to_updates: boolean
      accept_terms: boolean
    },
  ) {
    await http.post(`/api/v1/registrations/${activationKey}/complete`, payload)
  },
}
